import { useEffect, useState } from "react";
import { useAgent } from "../../agents";

/* Polls an agent's `used` flag until a client has connected for it.
 *
 * `used` flips immediately server-side on the agent's first x-agent-id request
 * (one-way latch), so it's the authoritative onboarding "we saw your traffic"
 * signal — faster than /summary, whose aggregation can lag several minutes.
 *
 * useAgent reads through the analytics useQuery cache, which refetches only when
 * its key changes; the key includes a `version`, so bumping `version` on an
 * interval is the lever that forces each re-poll. Ticks stop the moment `used`
 * is true and on unmount, so the number of salted cache entries is bounded by
 * how long the user takes to send a first request. */
export function useAgentUsedPoll(
  agentId: string,
  intervalMs = 5000,
): { used: boolean } {
  const [version, setVersion] = useState(0);
  const { data } = useAgent(agentId, version);
  const used = data?.used === true;
  const loaded = data !== undefined;

  // Tick only between resolved reads: don't start until the current read
  // lands. An agent already `used` on the first response then never starts an
  // interval, and a bump can't fire while a read is in flight — bumping
  // mid-flight re-keys the query and discards the pending response, which on
  // reads slower than the interval would starve `used` forever.
  useEffect(() => {
    if (!loaded || used) return;

    const id = setInterval(() => setVersion((v) => v + 1), intervalMs);
    return () => clearInterval(id);
  }, [loaded, used, intervalMs]);

  return { used };
}
